import {
    Icon,
    OfficeViewCallRecording,
    OfficeViewCallResults,
    OfficeViewMiddleHeader,
} from "dyl-components";
import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button, Form, Grid, Header, Loader } from "semantic-ui-react";
import NewActivityForm from "shared/forms/NewActivity";
import { PhoneUtil } from "utils";

const EditCall = ({
    onNewContactClick,
    control,
    editLogModal,
    onSave,
    onEmailClick,
    onTaskClick,
    onEventClick,
    onHotlistClick,
    onSaveDisabled,
    isInHotlist,
    isSelfReportCall,
    onClickToDial,
    relatedToOptions = [],
    onDirectCustomerClick
}) => {
    const call = editLogModal?.data;
    const {
        activeIntegration,
        isUpdatingCallResults,
        isGettingListOfTagCategories,
        listOfTagCategories,
        officeViewCreds
    } = useSelector((state) => {
        return {
            isUpdatingCallResults: state?.call_history.isUpdatingCallResults,
            activeIntegration: state?.oauth_integrations?.activeIntegration,
            listOfTagCategories: state?.tag_categories?.listOfTagCategories,
            isGettingListOfTagCategories: state?.tag_categories?.isGettingListOfTagCategories,
            officeViewCreds: state.officeView.creds,
        };
    });

    const tagsCategoryOptions = useMemo(() => {
        if (!listOfTagCategories?.length) return [];
        return listOfTagCategories?.map?.((category) => {
            return {
                text: category?.category,
                value: category?.id,
                key: category?.id,
                options: category?.tags?.map?.((tag) => ({
                    text: String(tag?.tag),
                    value: tag?.id,
                    key: tag?.id,
                })),
            };
        });
    }, [listOfTagCategories]);

    const [isCallActivityFormOpen, setIsCallActivityFormOpen] =
        React.useState(false);
    const isPhoneSystemSupported =
        PhoneUtil.isPhoneSystemSupported(officeViewCreds);

    const call_suspended = call?.call_suspended || call?.contacts?.[0]?.call_suspended;
    return (
        <div
            className="OfficeView__midContainer"
            style={{
                marginTop: "-3rem",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                overflow: "visible"
            }}
        >
            <OfficeViewMiddleHeader
                onNewContactClick={() => onNewContactClick?.(call)}
                person={{
                    name: call?.record?.name,
                    contact_id: call?.record?.contact_id,
                    account_id: call?.record?.account_id,
                    archived: call?.record?.archived,
                    contact_details: {
                        phone: call?.record?.phone ? [{ phone: call?.record?.phone }] : [],
                        email: call?.record?.email,
                        job_title: call?.record?.job_title,
                        pipeline_stage: call?.record?.pipeline_category,
                        direct_customer: call?.contacts?.[0]?.direct_customer,
                        call_suspended: call_suspended,
                    },
                    account_details: {
                        account_type: call?.record?.contact_type,
                        customer: call?.record?.is_customer,
                        phone: call?.record?.contact_id ? call?.record?.phone || "" : "",
                        call_suspended: call_suspended,
                    },
                }}
                unknown={{
                    unknown: !call?.record?.name,
                    name: call?.record?.name || call?.contacts?.[0]?.contact_name,
                }}
                onRemovePerson={editLogModal.closeModal}
                isInHotlist={isInHotlist}
                hasIntegration={Object.keys(activeIntegration || {}).length > 0}
                onEmailClick={onEmailClick}
                onTaskClick={onTaskClick}
                onEventClick={onEventClick}
                onHotlistClick={onHotlistClick}
                onClickToDial={onClickToDial}
                isPhoneSystemSupported={isPhoneSystemSupported}
                logActivityForm={(
                    contact,
                    emailOnly,
                    isContact,
                    onClose,
                    hideActivitySelection
                ) => {
                    return (
                        <NewActivityForm
                            contact={contact}
                            emailOnly={emailOnly}
                            isContact={isContact}
                            onClose={onClose}
                            hideActivitySelection={hideActivitySelection}
                        />
                    );
                }}
                setIsCallActivityFormOpen={setIsCallActivityFormOpen}
                isCallActivityFormOpen={isCallActivityFormOpen}
                onDirectCustomerClick={onDirectCustomerClick}
            />
            {isSelfReportCall ? (
                <div>
                    <Icon className="fa-solid fa-clock-rotate-left" />
                    Duration: {PhoneUtil.timeTicker(call?.length ?? 0)}
                </div>
            ) : (
                <OfficeViewCallRecording
                    currentCallPhoneNumber={call?.phone}
                    duration={call?.length}
                    callRecording={{
                        url: call?.call_recording,
                        sound_id: call?.call_uuid,
                    }}
                />
            )}
            {isSelfReportCall ? (
                <Grid className="OfficeViewCallResults" columns="equal">
                    <Grid.Row>
                        <Grid.Column width={4}>
                            <Controller
                                name="call_result"
                                control={control}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Form.Select
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange({
                                                target: { name, value },
                                            });
                                        }}
                                        placeholder="Call result"
                                        selectOnBlur={false}
                                        options={PhoneUtil.CALL_RESULT_OPTIONS}
                                        disabled={
                                            call?.direction !== "outbound"
                                        }
                                        className="CallResults__dropdown"
                                        search
                                        fluid
                                        scrolling
                                    />
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={14}>
                            <Controller
                                name="call_notes"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <>
                                        <Header
                                            as="h6"
                                            style={{ marginBottom: 8 }}
                                        >
                                            Call Notes
                                        </Header>
                                        <Form.TextArea
                                            onChange={(e) => {
                                                onChange(e);
                                            }}
                                            value={value}
                                            rows={4}
                                        />
                                    </>
                                )}
                            />
                        </Grid.Column>
                        <Grid.Column
                            verticalAlign="bottom"
                            style={{ marginBottom: 5 }}
                        >
                            <Button
                                fluid
                                onClick={onSave}
                                disabled={onSaveDisabled}
                                className="CallResults__saveButton"
                            >
                                {isUpdatingCallResults ? (
                                    <Loader inline active size="small" />
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            ) : (
                <OfficeViewCallResults
                    control={control}
                    callResultsDisabled={call?.direction !== "outbound"}
                    onSave={onSave}
                    onSaveDisabled={onSaveDisabled}
                    isSaveLoading={isUpdatingCallResults}
                    callTagOptions={tagsCategoryOptions}
                    callTagDisabled={isGettingListOfTagCategories}
                    relatedToOptions={relatedToOptions}
                />
            )}
        </div>
    );
};

export default EditCall;
